import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Check, Zap, ShieldCheck, Wallet, Clock, Sparkles,
  Building2, Dumbbell, Hotel, Briefcase, GraduationCap, Stethoscope, Warehouse,
  Plus, Minus, MapPin, Phone, Mail, CalendarClock
} from "lucide-react";
import { API } from "@/App";
import { PRODUCTS } from "@/data/products";
import { CRAVY_CONFIG } from "@/config";
import Calculator from "@/components/Calculator";

const LOGO = "/assets/cravy-logo.png";

const HERO_IMG = "/assets/starter-market.jpg";
// 6 main products surfaced on landing; full catalog lives on /products
const LANDING_PRODUCTS = PRODUCTS.filter((p) =>
  ["snack-beverage-combo", "imbera-vr27-qr", "us1200-ct-ultra", "taste-series", "stockwell-2", "medium-market"].includes(p.slug)
);

const USE_CASES = [
  { icon: Building2,      title: "Apartments",        desc: "Resident amenity that runs 24/7." },
  { icon: Briefcase,      title: "Corporate Offices", desc: "Keep your team fueled, no purchasing required." },
  { icon: Dumbbell,       title: "Gyms",              desc: "Post-workout protein and drinks on tap." },
  { icon: GraduationCap,  title: "Schools",           desc: "Healthy snacks within arm's reach." },
  { icon: Stethoscope,    title: "Medical Offices",   desc: "A calm, premium amenity for waiting patients." },
  { icon: Hotel,          title: "Hotels",            desc: "Guest convenience without a gift-shop overhead." },
  { icon: Warehouse,      title: "Warehouses",        desc: "Refuel without a long drive off-site." },
];

const FAQ_ITEMS = [
  { q: "What does it cost the property?",
    a: "Nothing. No installation fees, no monthly charges, no hidden costs. Cravy invests in the machine, the inventory, and the operations — your property hosts the amenity." },
  { q: "Who handles stocking and maintenance?",
    a: "We do — completely. Our local team restocks 1–2× per week with real-time inventory monitoring, and we handle every repair, often same-day." },
  { q: "What about liability?",
    a: "Cravy carries $2M general liability insurance plus workers' compensation. Your property has zero operational liability." },
  { q: "Can we choose what gets stocked?",
    a: "Absolutely. We curate a mix of premium snacks and beverages, and we welcome input from the property on what your residents, guests, or team would love most." },
  { q: "What payment methods are accepted?",
    a: "Card, tap-to-pay, Apple Pay, Google Pay, and cash. Modern checkout that works for everyone." },
  { q: "What's the commitment?",
    a: "Standard one-year term with a 30-day cancellation clause and no penalty. If it isn't working for your property, you're not stuck." },
];

const PROPERTY_TYPES = [
  "Apartment Community", "Corporate Office", "Gym / Fitness", "Hotel",
  "Medical Office", "School / University", "Warehouse / Industrial", "Other",
];

// ---------- Sub Components ----------

function Header() {
  const [open, setOpen] = useState(false);
  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-cravy-ink/80 border-b border-white/5">
      <nav className="max-w-7xl mx-auto px-5 lg:px-10 h-[80px] flex items-center justify-between">
        <button onClick={() => scrollTo("top")} className="flex items-center" data-testid="nav-logo">
          <img src={LOGO} alt="Cravy" className="h-14 w-auto object-contain" />
        </button>

        <div className="hidden md:flex items-center gap-7 text-cravy-cream/80 text-[14px] font-medium">
          <Link to="/products" className="hover:text-cravy-cream transition" data-testid="nav-products">Products</Link>
          <button onClick={() => scrollTo("how")} className="hover:text-cravy-cream transition" data-testid="nav-how">How it works</button>
          <button onClick={() => scrollTo("calculator")} className="hover:text-cravy-cream transition" data-testid="nav-calc">Calculator</button>
          <Link to="/residents" className="hover:text-cravy-cream transition" data-testid="nav-residents">Residents</Link>
          <Link to="/about" className="hover:text-cravy-cream transition" data-testid="nav-about">About</Link>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => scrollTo("partner")}
            className="btn-orange hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[14px]"
            data-testid="nav-cta-partner"
          >
            Partner with us <ArrowRight className="w-4 h-4" />
          </button>
          <button
            className="md:hidden p-2 text-cravy-cream"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            <div className="space-y-1.5">
              <div className="w-5 h-px bg-cravy-cream"></div>
              <div className="w-5 h-px bg-cravy-cream"></div>
              <div className="w-5 h-px bg-cravy-cream"></div>
            </div>
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden border-t border-white/5 bg-cravy-ink">
          <div className="px-5 py-4 space-y-3 text-cravy-cream/85 text-[15px]">
            <Link to="/products" onClick={() => setOpen(false)} className="block w-full text-left py-2" data-testid="mnav-products">Products</Link>
            <button onClick={() => scrollTo("how")} className="block w-full text-left py-2" data-testid="mnav-how">How it works</button>
            <button onClick={() => scrollTo("calculator")} className="block w-full text-left py-2" data-testid="mnav-calc">Calculator</button>
            <Link to="/residents" onClick={() => setOpen(false)} className="block w-full text-left py-2" data-testid="mnav-residents">Residents</Link>
            <Link to="/about" onClick={() => setOpen(false)} className="block w-full text-left py-2" data-testid="mnav-about">About</Link>
            <Link to="/refer" onClick={() => setOpen(false)} className="block w-full text-left py-2" data-testid="mnav-refer">Refer &amp; Earn</Link>
            <button onClick={() => scrollTo("partner")} className="btn-orange w-full px-5 py-3 rounded-full mt-2" data-testid="mnav-cta">Partner with us</button>
          </div>
        </div>
      )}
    </header>
  );
}

function HeroFeaturedCarousel() {
  // Rotating featured-market carousel that lives inside the hero card.
  const slides = PRODUCTS;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  const current = slides[idx];

  return (
    <div
      className="relative rounded-[22px] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-carousel"
    >
      {/* Slides */}
      <div className="relative aspect-[5/6] sm:aspect-[4/5] bg-white">
        {slides.map((s, i) => (
          <div
            key={s.slug}
            className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
            aria-hidden={i !== idx}
          >
            <img src={s.image} alt={s.name} className="w-full h-full object-contain p-6" />
          </div>
        ))}
      </div>

      {/* Caption pill (slides with the carousel) */}
      <Link
        to={`/products/${current.slug}`}
        className="absolute bottom-6 left-6 right-6 sm:right-auto inline-flex items-center justify-between sm:gap-6 bg-cravy-cream/95 backdrop-blur-sm text-cravy-ink rounded-2xl pl-4 pr-3 py-2.5 shadow-xl hover:bg-cravy-cream transition group"
        data-testid="hero-carousel-link"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-cravy-ink/55 font-semibold">{current.category}</div>
          <div className="font-display font-bold text-[16px] leading-tight">{current.name}</div>
        </div>
        <div className="ml-3 shrink-0 w-9 h-9 rounded-full bg-cravy-orange text-cravy-ink flex items-center justify-center group-hover:translate-x-0.5 transition">
          <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </div>
      </Link>

      {/* Dots */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
        {slides.map((s, i) => (
          <button
            key={s.slug}
            onClick={() => setIdx(i)}
            aria-label={`Show ${s.name}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-cravy-orange" : "w-1.5 bg-cravy-ink/30 hover:bg-cravy-ink/55"}`}
            data-testid={`hero-carousel-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section id="top" className="relative bg-cravy-ink cravy-grain pt-[80px] overflow-hidden">
      {/* Radial accent */}
      <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-cravy-orange/15 blur-[120px]" />
      <div className="absolute -bottom-40 -left-32 w-[460px] h-[460px] rounded-full bg-cravy-orangeDeep/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-12 lg:pt-20 pb-20 lg:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 rise">
          <h1 className="font-display font-extrabold text-cravy-cream tracking-[-0.03em] leading-[0.95] text-[44px] sm:text-[64px] lg:text-[80px]">
            Your space.
            <br />
            <span className="text-cravy-orange italic">Our market.</span>
            <br />
            <span className="scribble">Zero cost.</span>
          </h1>
          <p className="mt-7 text-cravy-cream/75 text-[17px] lg:text-[19px] max-w-xl leading-relaxed">
            Cravy installs, stocks, and maintains premium smart markets at residential communities, offices, gyms, and more — turning an empty corner into a 24/7 amenity your people will love.
            No installation fees. No monthly costs. No liability.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <button onClick={() => scrollTo("partner")} className="btn-orange inline-flex items-center gap-2 px-7 py-4 rounded-full text-[15px]" data-testid="hero-cta-partner">
              Request a Smart Market <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => scrollTo("how")} className="btn-ghost-dark inline-flex items-center gap-2 px-7 py-4 rounded-full text-[15px] font-semibold" data-testid="hero-cta-how">
              See how it works
            </button>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-cravy-cream/70 text-[13px]">
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-cravy-orange" /> Installed in ~3 days</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-cravy-orange" /> Fully managed</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-cravy-orange" /> $2M liability insured</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-cravy-orange" /> 30-day out, no penalty</div>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          {/* Main hero card with rotating featured market */}
          <div className="relative z-10 rounded-[28px] bg-gradient-to-br from-cravy-ink2 to-black/40 border border-white/5 p-3 shadow-2xl">
            <HeroFeaturedCarousel />
            <div className="absolute -top-4 -right-4 bg-cravy-orange text-cravy-ink rounded-full px-4 py-2 shadow-lg rotate-[5deg]">
              <div className="font-brush text-[20px] leading-none">Right when you need it.</div>
            </div>
            <div className="absolute -top-4 -left-4 bg-cravy-ink text-cravy-cream rounded-2xl px-3.5 py-2 shadow-xl border border-cravy-orange/30">
              <div className="text-[9px] uppercase tracking-[0.2em] text-cravy-orange font-bold">Open</div>
              <div className="font-display font-bold text-[14px] leading-none mt-0.5">24 / 7 / 365</div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="relative border-y border-white/5 bg-black/20 py-5 overflow-hidden">
        <div className="marquee-track text-cravy-cream/40 font-display uppercase tracking-[0.3em] text-[13px]">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center gap-12 pr-12">
              <span>Apartments</span><span className="text-cravy-orange">●</span>
              <span>Offices</span><span className="text-cravy-orange">●</span>
              <span>Gyms</span><span className="text-cravy-orange">●</span>
              <span>Hotels</span><span className="text-cravy-orange">●</span>
              <span>Schools</span><span className="text-cravy-orange">●</span>
              <span>Medical</span><span className="text-cravy-orange">●</span>
              <span>Warehouses</span><span className="text-cravy-orange">●</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", t: "We assess your space", d: "Quick site walk (in-person or photos) to find the perfect spot. Need is a 36\" × 29\" footprint near a standard outlet." },
    { n: "02", t: "We install in ~3 days",  d: "Our local team handles delivery, setup, payment integration, and product loading. You don't lift a finger." },
    { n: "03", t: "We stock + monitor",    d: "Real-time inventory tracking restocks 1–2× per week. Curated mix of snacks, drinks, and grab-and-go favorites." },
    { n: "04", t: "We handle everything",  d: "Maintenance, refunds, technical issues — all on us. Same-day local support, $2M liability coverage." },
    { n: "05", t: "You enjoy the amenity", d: "Your residents, employees, or guests get a 24/7 premium market. You get a happier community at zero cost." },
  ];

  return (
    <section id="how" className="bg-cravy-cream py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">How it works</div>
            <h2 className="font-display font-extrabold text-[40px] sm:text-[56px] lg:text-[64px] leading-[0.95] tracking-[-0.03em] mt-3">
              From empty corner to <span className="italic text-cravy-orangeDeep">crowd favorite</span> in a day.
            </h2>
          </div>
          <p className="text-cravy-ink/70 max-w-md text-[16px] leading-relaxed">
            We do the work. You get the credit (and a serious resident-satisfaction boost). The five-step partnership looks like this:
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="group bg-white rounded-3xl p-6 border border-cravy-ink/5 hover:border-cravy-orange/40 transition-all hover:-translate-y-1 hover:shadow-xl" data-testid={`step-${i}`}>
              <div className="font-display font-extrabold text-cravy-orange text-[42px] leading-none">{s.n}</div>
              <div className="mt-5 font-display font-bold text-[19px] tracking-tight">{s.t}</div>
              <div className="mt-2 text-[14px] text-cravy-ink/70 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyCravy() {
  const items = [
    { icon: Wallet,      t: "Zero cost. Always.",   d: "No install fees. No monthly fees. No hidden charges. We invest, you host." },
    { icon: ShieldCheck, t: "Zero liability.",      d: "$2M general liability plus workers' comp. Your insurance carrier won't even blink." },
    { icon: Clock,       t: "Always open.",         d: "24/7 access for residents, employees, and guests — no staffing required." },
    { icon: Zap,         t: "Fully managed.",       d: "Stocking, cleaning, refunds, repairs. Same-day local support, every time." },
    { icon: Sparkles,    t: "Premium selection.",   d: "Curated brands and grab-and-go favorites. Healthy options and the good stuff." },
    { icon: Check,       t: "30-day out, no penalty.", d: "Standard 1-year agreement with a flexible exit. Try it. Love it. Or walk away." },
  ];
  return (
    <section id="why" className="bg-cravy-ink cravy-grain py-20 lg:py-24 relative overflow-hidden">
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full bg-cravy-orange/8 blur-[140px]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <div className="max-w-2xl">
          <div className="text-cravy-orange font-semibold text-[12px] uppercase tracking-[0.25em]">Why Cravy</div>
          <h2 className="font-display font-extrabold text-cravy-cream text-[40px] sm:text-[56px] lg:text-[64px] leading-[0.95] tracking-[-0.03em] mt-3">
            The best amenity is the one <span className="italic text-cravy-orange">you never have to think about.</span>
          </h2>
          <p className="mt-6 text-cravy-cream/70 text-[17px] leading-relaxed">
            We obsess over the operations so you don't have to. Your residents will love it. You'll forget it's even there.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
          {items.map((it, i) => (
            <div key={i} className="rounded-3xl border border-white/8 bg-white/[0.03] p-7 hover:bg-white/[0.06] hover:border-cravy-orange/30 transition-all" data-testid={`why-${i}`}>
              <div className="w-11 h-11 rounded-2xl bg-cravy-orange/15 text-cravy-orange flex items-center justify-center">
                <it.icon className="w-5 h-5" />
              </div>
              <div className="mt-5 font-display font-bold text-cravy-cream text-[20px] tracking-tight">{it.t}</div>
              <div className="mt-2 text-cravy-cream/65 text-[14px] leading-relaxed">{it.d}</div>
            </div>
          ))}
        </div>

        {/* Stat strip */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { k: "$0", l: "Cost to the property" },
            { k: "~3 days", l: "Install timeline" },
            { k: "$2M", l: "Liability insurance" },
            { k: "300+", l: "Items per machine" },
          ].map((s, i) => (
            <div key={i} className="border-t border-white/10 pt-5" data-testid={`stat-${i}`}>
              <div className="font-display font-extrabold text-cravy-cream text-[40px] lg:text-[52px] leading-none">{s.k}</div>
              <div className="mt-2 text-cravy-cream/55 text-[13px] uppercase tracking-[0.15em]">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductLineup() {
  return (
    <section id="lineup" className="bg-cravy-cream2 py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">Our lineup</div>
            <h2 className="font-display font-extrabold text-[40px] sm:text-[56px] leading-[0.95] tracking-[-0.03em] mt-3">
              Markets that fit any space.
            </h2>
          </div>
          <div className="lg:max-w-md flex flex-col sm:items-end gap-4">
            <p className="text-cravy-ink/70 text-[16px] leading-relaxed">
              From compact coolers to full open-concept micro markets, we match the right format to your foot traffic and footprint.
            </p>
            <Link to="/products" className="inline-flex items-center gap-2 text-cravy-orangeDeep font-semibold text-[14px] hover:gap-3 transition-all" data-testid="lineup-view-all">
              View full catalog <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {LANDING_PRODUCTS.map((p, i) => (
            <Link
              key={p.slug}
              to={`/products/${p.slug}`}
              className="group bg-white rounded-3xl p-5 border border-cravy-ink/5 hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col"
              data-testid={`product-${i}`}
            >
              <div className="aspect-square rounded-2xl bg-white flex items-center justify-center overflow-hidden relative">
                <img src={p.image} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                {p.badge && (
                  <div className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full bg-cravy-orange text-cravy-ink text-[10px] font-bold uppercase tracking-wider shadow-md">
                    {p.badge}
                  </div>
                )}
                <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cravy-orange/15 text-cravy-orangeDeep text-[10px] font-bold uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-cravy-orange animate-pulse" /> In rotation
                </div>
              </div>
              <div className="mt-4 flex items-end justify-between gap-3">
                <div>
                  <div className="text-cravy-orangeDeep text-[10px] font-bold uppercase tracking-[0.15em]">{p.category}</div>
                  <div className="mt-1 font-display font-bold text-[17px] leading-tight tracking-tight">{p.name}</div>
                </div>
                <div className="shrink-0 w-8 h-8 rounded-full bg-cravy-cream2 text-cravy-orangeDeep flex items-center justify-center group-hover:bg-cravy-orange group-hover:text-cravy-ink transition-colors">
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Locations() {
  return (
    <section id="locations" className="bg-cravy-cream py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="max-w-2xl">
          <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">Where Cravy belongs</div>
          <h2 className="font-display font-extrabold text-[40px] sm:text-[56px] leading-[0.95] tracking-[-0.03em] mt-3">
            Every place that wants <span className="italic text-cravy-orangeDeep">happier</span> people.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {USE_CASES.map((u, i) => (
            <div key={i} className="group relative bg-white rounded-3xl p-6 border border-cravy-ink/5 hover:bg-cravy-ink hover:text-cravy-cream transition-all duration-300" data-testid={`use-${i}`}>
              <u.icon className="w-7 h-7 text-cravy-orangeDeep group-hover:text-cravy-orange transition" />
              <div className="mt-5 font-display font-bold text-[18px]">{u.title}</div>
              <div className="mt-1.5 text-[13px] text-cravy-ink/65 group-hover:text-cravy-cream/65 transition leading-relaxed">{u.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonial() {
  return (
    <section className="bg-cravy-ink cravy-grain py-20 lg:py-24 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-cravy-orange/8 blur-[160px]" />
      <div className="relative max-w-4xl mx-auto px-5 lg:px-10 text-center">
        <div className="font-brush text-cravy-orange text-[60px] leading-none">"</div>
        <blockquote className="font-display font-medium text-cravy-cream text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.15] tracking-[-0.02em]">
          The best amenity makes your residents happy, costs you nothing, and never shows up in your inbox.
        </blockquote>
        <div className="mt-8 flex items-center justify-center gap-3 text-cravy-cream/60 text-[14px]">
          <div className="w-10 h-px bg-cravy-orange/50" />
          <span className="font-semibold tracking-wide">The Cravy philosophy</span>
          <div className="w-10 h-px bg-cravy-orange/50" />
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="bg-cravy-cream py-20 lg:py-24">
      <div className="max-w-4xl mx-auto px-5 lg:px-10">
        <div className="text-center mb-12">
          <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">Frequently Asked</div>
          <h2 className="font-display font-extrabold text-[40px] sm:text-[56px] leading-[0.95] tracking-[-0.03em] mt-3">
            Questions, answered.
          </h2>
        </div>
        <div className="rounded-3xl border border-cravy-ink/8 bg-white overflow-hidden">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className="accordion-item last:border-b-0">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left hover:bg-cravy-cream2/40 transition"
                data-testid={`faq-q-${i}`}
              >
                <span className="font-display font-semibold text-[17px] sm:text-[19px] tracking-tight">{item.q}</span>
                <span className="shrink-0 w-9 h-9 rounded-full bg-cravy-orange/15 text-cravy-orangeDeep flex items-center justify-center">
                  {open === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </span>
              </button>
              {open === i && (
                <div className="px-6 pb-6 -mt-1 text-cravy-ink/70 text-[15px] leading-relaxed" data-testid={`faq-a-${i}`}>
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PartnerForm() {
  const [form, setForm] = useState({
    property_name: "", contact_name: "", email: "", phone: "",
    property_type: PROPERTY_TYPES[0], address: "", occupancy: "", foot_traffic: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Request received! We'll reach out within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="partner" className="bg-cravy-ink cravy-grain py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-cravy-orange/12 blur-[140px]" />
        <div className="relative max-w-3xl mx-auto px-5 lg:px-10 text-center" data-testid="partner-done">
          <div className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-cravy-orange text-cravy-ink mb-6">
            <Check className="w-8 h-8" strokeWidth={3} />
          </div>
          <h2 className="font-display font-extrabold text-cravy-cream text-[40px] sm:text-[56px] tracking-[-0.03em] leading-[0.95]">
            We've got it from here.
          </h2>
          <p className="mt-6 text-cravy-cream/70 text-[17px] leading-relaxed">
            Thanks for partnering with Cravy. Jayson or Ted will reach out within one business day to schedule a 10-minute walkthrough and find the perfect spot for your market.
          </p>
          <button
            onClick={() => { setDone(false); setForm({ ...form, property_name: "", contact_name: "", email: "", phone: "", address: "", occupancy: "", foot_traffic: "", message: "" }); }}
            className="mt-8 btn-ghost-dark inline-flex px-6 py-3 rounded-full text-[14px] font-semibold"
            data-testid="partner-reset"
          >
            Submit another property
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="partner" className="bg-cravy-ink cravy-grain py-20 lg:py-24 relative overflow-hidden">
      <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-cravy-orangeDeep/12 blur-[140px]" />
      <div className="absolute -bottom-20 -right-20 w-[420px] h-[420px] rounded-full bg-cravy-orange/12 blur-[140px]" />
      <div className="relative max-w-6xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="text-cravy-orange font-semibold text-[12px] uppercase tracking-[0.25em]">Partner with Cravy</div>
          <h2 className="font-display font-extrabold text-cravy-cream text-[40px] sm:text-[56px] leading-[0.95] tracking-[-0.03em] mt-3">
            Let's get your <span className="italic text-cravy-orange">market</span> in motion.
          </h2>
          <p className="mt-6 text-cravy-cream/70 text-[16px] leading-relaxed">
            Tell us a little about your property. We'll respond within one business day with next steps, and walk you through everything in about 10 minutes.
          </p>
          <div className="mt-10 space-y-4 text-cravy-cream/75 text-[14px]">
            <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-cravy-orange" /> Jayson Love · <a className="hover:text-cravy-cream" href="tel:13149138897">(314) 913-8897</a></div>
            <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-cravy-orange" /> <a className="hover:text-cravy-cream" href="mailto:Jayson@cravyco.com">Jayson@cravyco.com</a></div>
            <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-cravy-orange" /> Ted Kemp · <a className="hover:text-cravy-cream" href="tel:13147058356">(314) 705-8356</a></div>
            <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-cravy-orange" /> <a className="hover:text-cravy-cream" href="mailto:Ted@cravyco.com">Ted@cravyco.com</a></div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 rounded-3xl border border-white/8 bg-white/[0.03] p-7 lg:p-9 backdrop-blur-sm" data-testid="partner-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Property / Business name *">
              <input required value={form.property_name} onChange={update("property_name")} className="cravy-input" placeholder="The Bowery Lofts" data-testid="input-property-name" />
            </Field>
            <Field label="Property type *">
              <select required value={form.property_type} onChange={update("property_type")} className="cravy-input" data-testid="input-property-type">
                {PROPERTY_TYPES.map((t) => <option key={t} value={t} className="bg-cravy-ink2 text-cravy-cream">{t}</option>)}
              </select>
            </Field>
            <Field label="Your name *">
              <input required value={form.contact_name} onChange={update("contact_name")} className="cravy-input" placeholder="Jane Doe" data-testid="input-contact-name" />
            </Field>
            <Field label="Email *">
              <input required type="email" value={form.email} onChange={update("email")} className="cravy-input" placeholder="jane@bowerylofts.com" data-testid="input-email" />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={update("phone")} className="cravy-input" placeholder="(555) 123-4567" data-testid="input-phone" />
            </Field>
            <Field label="Occupancy / Headcount">
              <input value={form.occupancy} onChange={update("occupancy")} className="cravy-input" placeholder="220 units" data-testid="input-occupancy" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Property address">
                <input value={form.address} onChange={update("address")} className="cravy-input" placeholder="123 Main St, Austin, TX" data-testid="input-address" />
              </Field>
            </div>
            <Field label="Estimated daily foot traffic">
              <input value={form.foot_traffic} onChange={update("foot_traffic")} className="cravy-input" placeholder="~300 / day" data-testid="input-foot-traffic" />
            </Field>
            <Field label="Best spot for the machine?">
              <input value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="cravy-input" placeholder="Lobby near elevators" data-testid="input-spot" />
            </Field>
          </div>
          <button type="submit" disabled={submitting} className="btn-orange mt-7 inline-flex items-center gap-2 px-8 py-4 rounded-full text-[15px] disabled:opacity-60 disabled:cursor-not-allowed" data-testid="partner-submit">
            {submitting ? "Sending…" : "Request a Smart Market"} <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-4 flex items-center gap-4 text-cravy-cream/55 text-[13px]">
            <div className="flex-1 h-px bg-cravy-cream/10" />
            <span className="uppercase tracking-[0.18em] text-[11px] font-semibold">Or</span>
            <div className="flex-1 h-px bg-cravy-cream/10" />
          </div>
          <a
            href={CRAVY_CONFIG.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-[15px] font-semibold border border-cravy-orange/50 text-cravy-orange hover:bg-cravy-orange/10 transition"
            data-testid="partner-calendly"
          >
            <CalendarClock className="w-4 h-4" /> Book a 10-min call instead
          </a>
          <p className="mt-4 text-cravy-cream/45 text-[12px]">
            By submitting you agree to be contacted by Cravy about your inquiry. No spam, ever.
          </p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-cravy-cream/70 text-[12px] uppercase tracking-[0.15em] mb-2 font-semibold">{label}</span>
      {children}
    </label>
  );
}

function Footer() {
  return (
    <footer className="bg-cravy-ink border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center">
            <img src={LOGO} alt="Cravy" className="h-14 w-14 object-contain" />
          </div>
          <p className="mt-6 text-cravy-cream/60 text-[14px] max-w-sm leading-relaxed">
            Premium smart markets installed, stocked, and managed at no cost to your property. Operated by TK Love LLC.
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="text-cravy-cream/40 text-[11px] uppercase tracking-[0.2em] mb-4">Company</div>
          <ul className="space-y-3 text-cravy-cream/75 text-[14px]">
            <li><Link to="/products" className="hover:text-cravy-orange transition">Products</Link></li>
            <li><Link to="/residents" className="hover:text-cravy-orange transition">For Residents</Link></li>
            <li><Link to="/about" className="hover:text-cravy-orange transition">About</Link></li>
            <li><Link to="/refer" className="hover:text-cravy-orange transition">Refer &amp; Earn</Link></li>
            <li><button onClick={() => document.getElementById("calculator")?.scrollIntoView({behavior:"smooth"})} className="hover:text-cravy-orange transition">Impact Calculator</button></li>
            <li><button onClick={() => document.getElementById("faq")?.scrollIntoView({behavior:"smooth"})} className="hover:text-cravy-orange transition">FAQ</button></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="text-cravy-cream/40 text-[11px] uppercase tracking-[0.2em] mb-4">Contact</div>
          <ul className="space-y-3 text-cravy-cream/75 text-[14px]">
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-cravy-orange" /> Jayson · <a className="hover:text-cravy-cream" href="tel:13149138897">(314) 913-8897</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-cravy-orange" /> <a className="hover:text-cravy-cream" href="mailto:Jayson@cravyco.com">Jayson@cravyco.com</a></li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-cravy-orange" /> Ted · <a className="hover:text-cravy-cream" href="tel:13147058356">(314) 705-8356</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-cravy-orange" /> <a className="hover:text-cravy-cream" href="mailto:Ted@cravyco.com">Ted@cravyco.com</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-cravy-cream/40 text-[12px]">
          <div>© {new Date().getFullYear()} TK Love LLC · Cravy is a trademark of TK Love LLC.</div>
          <Link to="/admin/login" className="hover:text-cravy-cream/70 transition" data-testid="footer-admin">Admin</Link>
        </div>
      </div>
    </footer>
  );
}

// ---------- Page ----------
export default function Landing() {
  useEffect(() => {
    document.title = "Cravy — Smart Markets · Right when you need it.";
    // Handle hash navigation from other pages (e.g. /#partner from a product page)
    const hash = window.location.hash;
    if (hash) {
      const id = hash.slice(1);
      // Wait for layout to settle
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, []);
  return (
    <div className="font-body bg-cravy-cream min-h-screen">
      <Header />
      <Hero />
      <HowItWorks />
      <WhyCravy />
      <Calculator />
      <Locations />
      <FAQ />
      <PartnerForm />
      <Footer />
    </div>
  );
}
